import { Layout, icons, labelDefaultMediumStyles, labelDefaultRegularStyles } from '@lifebit-ai/lds'
import { ResultsState } from 'enums'
import styled, { css } from 'styled-components'

import { H1 } from 'components/H1/H1'
import { CommonContainer } from 'core/Common.styles'
import { LabelTag } from 'ui/labelTag/labelTag.styles'
import { getStatusIconColor } from 'utils/styles'

export const StyledContent = styled(Layout.Content)`
  > div {
    gap: ${({ theme }) => theme.size.space['04']};
  }
`

export const TitleSkeletonWrapper = styled.div`
  width: 320px;
`

export const SubtitleSkeletonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.size.space['05']};
`

// LDS's Header block-stacks its title row and subtitle with no gap; this wrapper
// separates the HeaderTitleRow from the HeaderSubtitle content.
export const HeaderSubtitle = styled.div`
  margin-top: ${({ theme }) => theme.size.space['04']};
`

export const TagsWrapper = styled.div`
  ${labelDefaultRegularStyles};
  color: ${({ theme }) => theme.color.content.secondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.size.space['05']};
`

export const TagWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.size.space['02']};
`

export const TagLabel = styled.div<{ $disabled?: boolean }>`
  ${labelDefaultRegularStyles};
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.size.space['02']};
  color: ${({ theme, $disabled }) => ($disabled ? theme.color.content.secondary : theme.color.content.main)};
`

export const UnassignedTag = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.size.space['02']};
  color: ${({ theme }) => theme.color.content.secondary};
`

export const TagIcon = styled.div`
  display: flex;
  align-items: center;

  svg {
    path {
      fill: ${({ theme }) => theme.color.content.secondary};
    }
  }
`

export const ButtonsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.size.space['04']};
  max-width: fit-content;
`

export const AddLabelButton = styled.button`
  appearance: none;
  align-items: center;
  background-color: transparent;
  border: none;
  color: inherit;
  display: flex;
  font: inherit;
  gap: ${({ theme }) => theme.size.space['02']};
  padding: 0;
  cursor: pointer;

  /* The plus ships with its own dark fill; match it to the label beside it. */
  svg path,
  svg g {
    fill: ${({ theme }) => theme.color.content.secondary};
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled svg path,
  &:disabled svg g {
    fill: ${({ theme }) => theme.color.content.disabled};
  }

  &:disabled span {
    color: ${({ theme }) => theme.color.content.disabled};
  }
`

export const DisabledAddLabelButtonWrapper = styled.span`
  display: inline-flex;
`

export const LabelsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.size.space['03']};
`

export const StyledLabelTag = styled(LabelTag)`
  ${labelDefaultMediumStyles};
  align-items: center;
  cursor: default;
  display: flex;
  gap: ${({ theme }) => theme.size.space['02']};

  > svg {
    cursor: pointer;
  }
`

export const CloseIcon = styled(icons.CloseSmall)`
  &:hover {
    transform: scale(1.1);
  }
`

export const ContentsTitle = styled.div`
  font-size: ${({ theme }) => theme.font.heading.fontSize.small};
  font-weight: ${({ theme }) => theme.font.heading.fontWeight.medium};
  color: ${({ theme }) => theme.color.content.secondary};
  display: flex;
  align-items: center;
`

export const GoUpContainer = styled.div`
  cursor: pointer;
  display: flex;
`

export const GoUpIconContainer = styled.div`
  display: flex;
  margin-right: ${({ theme }) => theme.size.space['02']};

  svg {
    width: ${({ theme }) => theme.size.icon.medium};
    height: ${({ theme }) => theme.size.icon.medium};

    path {
      fill: ${({ theme }) => theme.color.content.secondary};
    }
  }
`

export const StatusIconWrapper = styled.div<{ status: ResultsState }>`
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.size.space['01']};

  svg {
    width: ${({ theme }) => theme.size.icon.small};
    height: ${({ theme }) => theme.size.icon.small};

    path {
      fill: ${({ status, theme }) => getStatusIconColor(status, theme)};
    }
  }
`

export const StatusWrapper = styled.div`
  ${labelDefaultRegularStyles};
  align-items: center;
  color: ${({ theme }) => theme.color.content.main};
  display: flex;
`

export const ReportMessage = styled(H1)`
  font-weight: ${({ theme }) => theme.font.weight.regular};
  font-size: ${({ theme }) => theme.size.font['2-xsmall']};
  line-height: ${({ theme }) => theme.font.lineheight['02']};
  text-align: center;
`

export const StyledReportMessage = styled(CommonContainer)`
  display: flex;
  justify-content: center;
  height: 152px;
  align-items: center;
`

export const ReportWrapper = styled.div`
  padding-top: ${({ theme }) => theme.size.space['07']};
  height: 90vh;
`

export const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.space['05']};
  height: 100%;
  background: ${({ theme }) => theme.color.background.surface};
  box-shadow: ${({ theme }) => theme.shadow['03']};
  border-radius: ${({ theme }) => theme.size.border.radius['04']};
  padding: ${({ theme }) => theme.size.space['05']};
`

export const StyledNameContents = styled.div<{ isDir?: boolean }>`
  display: flex;
  align-items: center;

  ${({ isDir }) =>
    isDir &&
    css`
      &:hover {
        cursor: pointer;
      }
    `}

  svg {
    min-width: ${({ theme }) => theme.size.icon.medium};
    min-height: ${({ theme }) => theme.size.icon.medium};
    margin-right: ${({ theme }) => theme.size.space['03']};
    width: ${({ theme }) => theme.size.icon.medium};
    height: ${({ theme }) => theme.size.icon.medium};
  }
`

export const StyledResultName = styled.div`
  align-items: center;
  display: flex;
  width: 50%;
`

export const StyledResultOwner = styled.div`
  width: 23%;
  cursor: pointer;
  display: flex;
  align-items: center;
`

export const StyledAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  gap: ${({ theme }) => theme.size.space['04']};
  width: 70px;
  margin: 0 ${({ theme }) => theme.size.space['04']};

  path,
  g {
    fill: ${({ theme }) => theme.color.content.main};
  }
`

const iconCss = css`
  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }
`

export const CopyUrlIcon = styled(icons.LinkSmall)`
  ${iconCss};
`

export const ViewDetailsIcon = styled(icons.EyeSmall)`
  ${iconCss};
`

export const PencilIcon = styled(icons.PencilSmall)`
  ${iconCss};

  path {
    fill: ${({ theme }) => theme.color.content.secondary};
  }
`

export const ApprovalModalDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.layout['01']};
`

export const AlertContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.layout['01']};
`

export const LabelText = styled.span`
  ${labelDefaultMediumStyles};
  color: ${({ theme }) => theme.color.content.secondary};
`
